import { useContext } from "react";
import { GlobalIcon, IconNameT } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../../context/i18n.context";
import { PolicyI } from "../../../../../../api/request/Lead/Interfaces/PolicyInterface";
import numberFormat from "../../../../../../utils/Utils";
import { cardStyle, sectionTitleStyle } from "../cardStyles";
import PendingCapitalLine from "../pendingCapital/PendingCapitalLine";

const GUARANTEE_ICONS: Record<string, IconNameT> = {
  DEATH: "ShieldCheckIcon",
  DISABILITY: "ShieldCheckIcon",
  SERIOUS_ILLNESS: "Heart2Icon",
  ACCIDENT_DEATH: "ShieldCheckIcon",
  ACCIDENT_DISABILITY: "ShieldCheckIcon",
  TEMPORARY_DISABILITY: "ShieldCheckIcon",
  TRAFFIC_ACCIDENT_DEATH: "ShieldCheckIcon",
  TRAFFIC_ACCIDENT_DISABILITY: "ShieldCheckIcon",
};

interface CoverageSummaryCardProps {
  policy?: PolicyI;
  capital: number;
}

const CoverageSummaryCard = ({ policy, capital }: CoverageSummaryCardProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);

  return (
    <div style={cardStyle}>
      <div style={sectionTitleStyle}>
        <GlobalIcon iconName="ShieldCheckIcon" size="XS" color="#4f46e5" />
        <h2 style={{ fontSize: 18, fontWeight: 700, color: "#111827", margin: 0 }}>
          {translate("newDashboard.policyPage.coverageSummary")}
        </h2>
      </div>

      {/* Capital */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          padding: 20,
          backgroundColor: "#eef2ff",
          borderRadius: 10,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 48,
            height: 48,
            backgroundColor: "#4f46e5",
            borderRadius: 12,
            flexShrink: 0,
          }}
        >
          <GlobalIcon iconName="ShieldLockIcon" size="XS" color="#fff" />
        </div>
        <div>
          <p style={{ fontSize: 13, fontWeight: 500, color: "#6366f1", margin: 0 }}>
            {translate("newDashboard.policyPage.assuredCapital")}
          </p>
          <p style={{ fontSize: 28, fontWeight: 800, color: "#312e81", margin: 0 }}>
            {numberFormat(capital, "capital")}
          </p>
          <PendingCapitalLine pendingCapital={policy?.pendingCapital} current={capital} />
        </div>
      </div>

      {/* Guarantees */}
      {policy?.traits && policy.traits.length > 0 && (
        <div>
          <p
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#6b7280",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              margin: "0 0 12px",
            }}
          >
            {translate("newDashboard.policyPage.activeGuarantees")}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {policy.traits.map((t, idx) => {
              const iconName = GUARANTEE_ICONS[t.trait] || "ShieldCheckIcon";
              const label =
                translate(`newDashboard.policyPage.guarantee.${t.trait}`) ||
                t.trait;
              const desc = translate(
                `newDashboard.policyPage.guarantee.${t.trait}.desc`
              );
              return (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: 16,
                    backgroundColor: "#f9fafb",
                    borderRadius: 8,
                    border: "1px solid #f3f4f6",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 36,
                      height: 36,
                      backgroundColor: "#ecfdf5",
                      borderRadius: 8,
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                  >
                    <GlobalIcon iconName={iconName} size="XS" color="#059669" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      <p style={{ fontSize: 15, fontWeight: 600, color: "#111827", margin: 0 }}>
                        {label}
                      </p>
                      <span style={{ fontSize: 14, fontWeight: 700, color: "#059669" }}>
                        {numberFormat(t.capital, "capital")}
                      </span>
                    </div>
                    <PendingCapitalLine
                      pendingCapital={policy.pendingCapital}
                      current={t.capital}
                      trait={t.trait}
                      style={{ textAlign: "right" }}
                    />
                    {desc && (
                      <p style={{ fontSize: 13, color: "#6b7280", margin: "4px 0 0" }}>
                        {desc}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CoverageSummaryCard;
